import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Tab,
  Tabs
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Payment as PaymentIcon,
  Category as CategoryIcon,
  Receipt as ReceiptIcon,
  Undo as ReturnIcon,
  History as HistoryIcon,
  Money as MoneyIcon,
  CreditCard as CreditCardIcon,
  PhoneIphone as PhoneIphoneIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { InventoryItem, SaleItem, PaymentMethod, PaymentStatus, Branch, Warehouse, RefundMethod } from '../utils/types';
import { Category } from '../types/category';
import { Customer } from '../types/customer';
import { getAllInventoryItems } from '../services/inventoryService';
import { createSale, getSalesByBranch, createFullReturn, createPartialReturn, debugReturns } from '../services/posService';
import { useAuth } from '../contexts/AuthContext';
import { getAllBranches } from '../services/branchService';
import { getAllWarehouses } from '../services/inventoryService';
import { DEFAULT_CURRENCY_SYMBOL } from '../utils/constants';
import { formatCurrency } from '../utils/numberUtils';
import { getAllCustomers, addCustomer } from '../services/customerService';
import { getMainCategories } from '../services/categoryService';
import { canAccessWarehouse } from '../services/permissionService';
import InvoiceDialog from '../components/pos/InvoiceDialog';
import ReturnDialog from '../components/pos/ReturnDialog';
import EnhancedReturnDialog from '../components/pos/EnhancedReturnDialog';
import { createReturn } from '../services/posService';

const POSPage: React.FC = () => {
  const { currentUser } = useAuth();

  // حالة المنتجات
  const [products, setProducts] = useState<InventoryItem[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<InventoryItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  // حالة الفئات
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

  // حالة سلة المشتريات
  const [cartItems, setCartItems] = useState<{
    product: InventoryItem;
    quantity: number;
    total: number;
  }[]>([]);

  // حالة الفاتورة
  const [customerName, setCustomerName] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [walletNumber, setWalletNumber] = useState('');
  const [discount, setDiscount] = useState(0);
  const [processingOrder, setProcessingOrder] = useState(false);
  const [amountPaid, setAmountPaid] = useState<number>(0);
  const [isPartialPayment, setIsPartialPayment] = useState(false);
  const [dueDate, setDueDate] = useState<string>('');
  const [invoiceType, setInvoiceType] = useState<'retail' | 'wholesale'>('retail'); // نوع الفاتورة: عادية أو جملة

  // المحافظ الإلكترونية
  const electronicWalletMethods = ['vodafone_wallet', 'instapay', 'etisalat_wallet'];

  // حالة الحوارات
  const [openAddCustomerDialog, setOpenAddCustomerDialog] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });

  // الفروع والمخازن
  const [branches, setBranches] = useState<Branch[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);

  // حالة رسالة التنبيه
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // حوارات الفواتير والمرتجعات
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState<any>(null);
  const [recentSales, setRecentSales] = useState<any[]>([]);

  // تحميل المنتجات والفروع والمخازن والعملاء والفئات عند تحميل الصفحة
  useEffect(() => {
    // تحميل جميع البيانات بترتيب معين
    const loadAllData = async () => {
      try {
        // تحميل الفئات أولاً
        await loadCategories();

        // ثم تحميل المنتجات (التي تعتمد على الفئات)
        await loadProducts();

        // تحميل البيانات الأخرى بالتوازي
        await Promise.all([
          loadBranchesAndWarehouses(),
          loadCustomers()
        ]);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };

    loadAllData();
  }, []);

  // تحميل الفئات
  const loadCategories = async () => {
    try {
      console.log('بدء تحميل الفئات...');
      const categoriesData = await getMainCategories(true);
      console.log('تم تحميل الفئات الخام:', categoriesData.length);

      // التحقق من تنسيق البيانات
      if (categoriesData.length > 0) {
        console.log('نموذج للفئات المستلمة:', categoriesData[0]);
      }

      const activeCategories = categoriesData.filter(cat => cat.isActive !== false);
      console.log('الفئات النشطة:', activeCategories.length);

      setCategories(activeCategories);

      // إذا كانت الفئات فارغة، حاول استخدام طريقة مختلفة للتحميل
      if (activeCategories.length === 0) {
        console.log('لا توجد فئات نشطة، محاولة تحميل الفئات بطريقة أخرى');
        try {
          // استخدام التحميل بدون إجبار التحديث
          const fallbackCategories = await getMainCategories(false);
          console.log('الفئات الاحتياطية:', fallbackCategories.length);

          if (fallbackCategories.length > 0) {
            setCategories(fallbackCategories);
          } else {
            // إذا فشلت كل المحاولات، استخدم بيانات مخزنة مسبقًا للاختبار
            const hardcodedCategories = [
              { id: 'cat1', name: 'هواتف', isActive: true, createdAt: new Date(), updatedAt: new Date() },
              { id: 'cat2', name: 'أجهزة لوحية', isActive: true, createdAt: new Date(), updatedAt: new Date() },
              { id: 'cat3', name: 'لابتوب', isActive: true, createdAt: new Date(), updatedAt: new Date() },
              { id: 'cat4', name: 'إكسسوارات', isActive: true, createdAt: new Date(), updatedAt: new Date() },
            ];
            console.log('استخدام فئات مخزنة مسبقًا للاختبار:', hardcodedCategories.length);
            setCategories(hardcodedCategories);
          }
        } catch (fallbackError) {
          console.error('فشل في تحميل الفئات الاحتياطية:', fallbackError);

          // إذا فشل كل شيء، استخدم البيانات المخزنة مسبقًا
          const hardcodedCategories = [
            { id: 'cat1', name: 'هواتف', isActive: true, createdAt: new Date(), updatedAt: new Date() },
            { id: 'cat2', name: 'أجهزة لوحية', isActive: true, createdAt: new Date(), updatedAt: new Date() },
            { id: 'cat3', name: 'لابتوب', isActive: true, createdAt: new Date(), updatedAt: new Date() },
            { id: 'cat4', name: 'إكسسوارات', isActive: true, createdAt: new Date(), updatedAt: new Date() },
          ];
          console.log('استخدام فئات مخزنة مسبقًا للاختبار بعد خطأ:', hardcodedCategories.length);
          setCategories(hardcodedCategories);
        }
      }

      return activeCategories.length > 0 ? activeCategories : categoriesData;
    } catch (error) {
      console.error('Error loading categories:', error);

      // في حالة فشل كل المحاولات، استخدم بيانات مخزنة مسبقًا للاختبار
      const hardcodedCategories = [
        { id: 'cat1', name: 'هواتف', isActive: true, createdAt: new Date(), updatedAt: new Date() },
        { id: 'cat2', name: 'أجهزة لوحية', isActive: true, createdAt: new Date(), updatedAt: new Date() },
        { id: 'cat3', name: 'لابتوب', isActive: true, createdAt: new Date(), updatedAt: new Date() },
        { id: 'cat4', name: 'إكسسوارات', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      ];
      console.log('استخدام فئات مخزنة مسبقًا للاختبار بعد خطأ:', hardcodedCategories.length);
      setCategories(hardcodedCategories);

      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل الفئات، تم استخدام فئات افتراضية',
        severity: 'warning'
      });

      return hardcodedCategories;
    }
  };

  // تحميل الفروع والمخازن
  const loadBranchesAndWarehouses = async () => {
    try {
      console.log('🔍 Loading branches and warehouses...');
      console.log('👤 Current user:', currentUser);

      // تحميل الفروع
      const branchList = await getAllBranches();
      console.log('🏢 Branches loaded:', branchList);
      setBranches(branchList);

      // تعيين الفرع الافتراضي
      if (branchList.length > 0) {
        setSelectedBranch(branchList[0]);
      }

      // تحميل المخازن
      const warehouseList = await getAllWarehouses();
      console.log('🏪 All warehouses loaded:', warehouseList);

      // تصفية المخازن حسب صلاحيات المستخدم
      const allowedWarehouses = warehouseList.filter(warehouse => {
        const hasAccess = canAccessWarehouse(currentUser, warehouse.id);
        console.log(`🔐 Warehouse ${warehouse.name} (${warehouse.id}): hasAccess = ${hasAccess}`);
        return hasAccess;
      });

      console.log('✅ Allowed warehouses:', allowedWarehouses);
      setWarehouses(allowedWarehouses);

      // تعيين المخزن الافتراضي من المخازن المسموح بها
      if (allowedWarehouses.length > 0) {
        const defaultWarehouse = branchList.length > 0
          ? allowedWarehouses.find(w => w.branchId === branchList[0].id) || allowedWarehouses[0]
          : allowedWarehouses[0];

        console.log('🎯 Default warehouse selected:', defaultWarehouse);
        setSelectedWarehouse(defaultWarehouse);
      } else {
        // إذا لم يكن هناك مخازن مسموح بها، محاولة إصلاح الصلاحيات
        console.error('❌ No warehouses allowed for user');
        console.log('👤 User allowedWarehouses:', currentUser?.allowedWarehouses);
        console.log('👤 User role:', currentUser?.role);
        console.log('👤 User branchId:', currentUser?.branchId);
        
        // محاولة إصلاح الصلاحيات تلقائياً
        if (currentUser && currentUser.branchId) {
          console.log('🔧 Attempting to fix warehouse permissions...');
          try {
            const { updateUserWarehousesFromBranch } = await import('../services/userService');
            await updateUserWarehousesFromBranch(currentUser.id);
            
            // إعادة تحميل المستخدم بعد الإصلاح
            const { getUserById } = await import('../services/userService');
            const updatedUser = await getUserById(currentUser.id);
            if (updatedUser) {
              console.log('✅ User permissions updated, reloading warehouses...');
              // إعادة تحميل المخازن مع الصلاحيات المحدثة
              const fixedAllowedWarehouses = warehouseList.filter(warehouse =>
                canAccessWarehouse(updatedUser, warehouse.id)
              );
              
              if (fixedAllowedWarehouses.length > 0) {
                setWarehouses(fixedAllowedWarehouses);
                setSelectedWarehouse(fixedAllowedWarehouses[0]);
                setSnackbar({
                  open: true,
                  message: 'تم إصلاح صلاحيات المخازن تلقائياً',
                  severity: 'success'
                });
                return;
              }
            }
          } catch (fixError) {
            console.error('Error fixing warehouse permissions:', fixError);
          }
        }
        
        setSnackbar({
          open: true,
          message: 'ليس لديك صلاحية للوصول إلى أي مخزن. يرجى التواصل مع مدير النظام.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error loading branches and warehouses:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل بيانات الفروع والمخازن',
        severity: 'error'
      });
    }
  };

  // تحميل العملاء
  const loadCustomers = async () => {
    try {
      const customersList = await getAllCustomers();
      setCustomers(customersList);
    } catch (error) {
      console.error('Error loading customers:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل بيانات العملاء',
        severity: 'error'
      });
    }
  };

  // تحميل المبيعات
  const loadSales = async () => {
    try {
      if (selectedBranch) {
        const salesData = await getSalesByBranch(selectedBranch.id);
        // يمكن حفظ البيانات في state إذا لزم الأمر
        console.log('Sales loaded:', salesData.length);
      }
    } catch (error) {
      console.error('Error loading sales:', error);
    }
  };

  // تحميل جميع المنتجات
  const loadProducts = async () => {
    try {
      setLoading(true);
      const items = await getAllInventoryItems();

      // تحقق مما إذا كانت هناك منتجات
      if (items.length === 0) {
        console.log('لم يتم العثور على منتجات، استخدام منتجات افتراضية للاختبار');

        // استخدام المنتجات الافتراضية إذا لم تكن هناك منتجات متاحة
        const dummyProducts = getDummyProducts();

        // تطبيع المنتجات الافتراضية
        const normalizedDummyItems = dummyProducts.map(item => {
          const normalizedItem = { ...item };
          return normalizedItem;
        });

        setProducts(normalizedDummyItems);
        setFilteredProducts(normalizedDummyItems);
        setLoading(false);
        return;
      }

      // التأكد من أن جميع المنتجات لديها بيانات فئة صحيحة
      const normalizedItems = items.map(item => {
        // نسخة جديدة من المنتج للتعديل عليها
        const normalizedItem = { ...item };

        // التأكد من وجود حقل category (كنص)
        if (!normalizedItem.category && normalizedItem.categoryId) {
          // محاولة العثور على اسم الفئة من المعرّف
          const category = categories.find(cat => cat.id === normalizedItem.categoryId);
          if (category) {
            normalizedItem.category = category.name;
          }
        }

        // التأكد من وجود حقل categoryId (كمعرّف)
        if (!normalizedItem.categoryId && normalizedItem.category) {
          // محاولة العثور على معرّف الفئة من الاسم
          const category = categories.find(cat => cat.name === normalizedItem.category);
          if (category) {
            normalizedItem.categoryId = category.id;
          }
        }

        return normalizedItem;
      });

      // عرض المنتجات المتاحة للبيع فقط وذات الكمية أكبر من صفر
      const availableProducts = normalizedItems.filter(item => item.quantity > 0);
      console.log(`تم تحميل ${availableProducts.length} منتج متاح للبيع`);

      // فحص معلومات الفئة للتشخيص
      if (availableProducts.length > 0) {
        const categoryCounts: { [key: string]: number } = {};
        availableProducts.forEach(product => {
          const catId = product.categoryId || 'غير محدد';
          categoryCounts[catId] = (categoryCounts[catId] || 0) + 1;
        });
        console.log('توزيع المنتجات حسب الفئة:', categoryCounts);
      }

      setProducts(availableProducts);
      setFilteredProducts(availableProducts);
    } catch (error) {
      console.error('Error loading products:', error);

      // استخدام المنتجات الافتراضية في حالة الخطأ
      console.log('حدث خطأ أثناء تحميل المنتجات، استخدام منتجات افتراضية للاختبار');
      const dummyProducts = getDummyProducts();
      setProducts(dummyProducts);
      setFilteredProducts(dummyProducts);

      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل المنتجات، تم استخدام منتجات افتراضية',
        severity: 'warning'
      });
    } finally {
      setLoading(false);
    }
  };

  // الحصول على منتجات افتراضية للاختبار
  const getDummyProducts = (): InventoryItem[] => {
    // إنشاء بعض المنتجات الافتراضية المرتبطة بالفئات المتاحة
    return [
      {
        id: 'p1',
        name: 'آيفون 13',
        sku: 'IP13-2023',
        category: 'هواتف',
        categoryId: 'cat1',
        quantity: 10,
        purchasePrice: 10000,
        sellingPrice: 12000,
        minQuantity: 2,
        image: 'https://via.placeholder.com/150?text=iPhone',
        description: 'هاتف آيفون 13 الجديد',
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: 'p2',
        name: 'سامسونج جالاكسي S21',
        sku: 'SGS21-2023',
        category: 'هواتف',
        categoryId: 'cat1',
        quantity: 15,
        purchasePrice: 7000,
        sellingPrice: 9000,
        minQuantity: 3,
        image: 'https://via.placeholder.com/150?text=Samsung',
        description: 'هاتف سامسونج جالاكسي S21',
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: 'p3',
        name: 'آيباد برو',
        sku: 'IPADP-2023',
        category: 'أجهزة لوحية',
        categoryId: 'cat2',
        quantity: 5,
        purchasePrice: 12000,
        sellingPrice: 15000,
        minQuantity: 1,
        image: 'https://via.placeholder.com/150?text=iPad',
        description: 'آيباد برو الجديد',
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: 'p4',
        name: 'سامسونج تاب S7',
        sku: 'TABS7-2023',
        category: 'أجهزة لوحية',
        categoryId: 'cat2',
        quantity: 8,
        purchasePrice: 8000,
        sellingPrice: 11000,
        minQuantity: 2,
        image: 'https://via.placeholder.com/150?text=Tab',
        description: 'تابلت سامسونج تاب S7',
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: 'p5',
        name: 'ماك بوك برو',
        sku: 'MBP-2023',
        category: 'لابتوب',
        categoryId: 'cat3',
        quantity: 3,
        purchasePrice: 20000,
        sellingPrice: 25000,
        minQuantity: 1,
        image: 'https://via.placeholder.com/150?text=MacBook',
        description: 'ماك بوك برو الجديد',
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: 'p6',
        name: 'سماعات آيربودز',
        sku: 'AIRP-2023',
        category: 'إكسسوارات',
        categoryId: 'cat4',
        quantity: 20,
        purchasePrice: 2000,
        sellingPrice: 3000,
        minQuantity: 5,
        image: 'https://via.placeholder.com/150?text=AirPods',
        description: 'سماعات آيربودز اللاسلكية',
        createdAt: new Date(),
        updatedAt: new Date()
      }
    ];
  };

  // إعادة فلترة المنتجات إذا تغيرت الفئات
  useEffect(() => {
    if (categories.length > 0 && products.length > 0) {
      // تنفيذ إعادة فلترة المنتجات عند تحميل الفئات
      let filtered = products;

      if (selectedCategoryId) {
        filtered = filtered.filter(product =>
          product.categoryId === selectedCategoryId ||
          (product.category && categories.find(cat =>
            cat.id === selectedCategoryId && cat.name === product.category
          ))
        );
      }

      if (searchTerm.trim() !== '') {
        filtered = filtered.filter(product =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (product.category && product.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }

      setFilteredProducts(filtered);
    }
  }, [categories]);

  // تصفية المنتجات عند تغيير البحث
  useEffect(() => {
    let filtered = products;

    // تطبيق فلتر الفئة أولاً إذا كان هناك فئة محددة
    if (selectedCategoryId) {
      filtered = filtered.filter(product =>
        product.categoryId === selectedCategoryId ||
        (product.category && categories.find(cat =>
          cat.id === selectedCategoryId && cat.name === product.category
        ))
      );
    }

    // ثم تطبيق فلتر البحث
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.category && product.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredProducts(filtered);
  }, [searchTerm, products, selectedCategoryId]);

  // معالجة تغيير الفئة المحددة مع التمرير المباشر للمنتجات المصفاة
  const handleCategoryChange = (categoryId: string) => {
    // عرض تشخيصي لفهم المشكلة
    console.log("معالجة تغيير الفئة:", categoryId);
    console.log("الفئة المحددة حاليًا:", selectedCategoryId);
    console.log("عدد المنتجات الكلي:", products.length);

    // تبديل: إذا ضغط المستخدم على نفس الفئة المحددة حالياً، قم بإلغاء تحديدها
    const newCategoryId = categoryId === selectedCategoryId ? '' : categoryId;
    console.log("الفئة الجديدة المحددة:", newCategoryId);

    setSelectedCategoryId(newCategoryId);

    // تصفية المنتجات مباشرة لتسريع الاستجابة
    let filtered = [...products]; // نسخة جديدة من المنتجات

    // إذا تم تحديد فئة، قم بتطبيق الفلتر
    if (newCategoryId) {
      console.log("تصفية المنتجات حسب الفئة:", newCategoryId);

      // العثور على الفئة المحددة للتشخيص
      const selectedCategory = categories.find(c => c.id === newCategoryId);
      console.log("معلومات الفئة المحددة:", selectedCategory);

      // التصفية
      filtered = filtered.filter(product => {
        // للتشخيص، سجل بعض المنتجات من هذه الفئة
        if (product.categoryId === newCategoryId) {
          console.log("منتج من نفس الفئة:", product.name, "categoryId:", product.categoryId);
          return true;
        }

        // التحقق من حالة أخرى - تطابق الاسم
        if (product.category && selectedCategory && product.category === selectedCategory.name) {
          console.log("منتج من نفس الفئة (بالاسم):", product.name, "category:", product.category);
          return true;
        }

        return false;
      });

      console.log(`تم العثور على ${filtered.length} منتج في الفئة ${newCategoryId}`);
    }

    // تطبيق فلتر البحث إذا كان موجوداً
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.category && product.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredProducts(filtered);
  };

  // إضافة منتج إلى السلة
  // دالة للحصول على السعر المناسب بناء على نوع الفاتورة
  const getProductPrice = (product: InventoryItem): number => {
    if (invoiceType === 'wholesale') {
      // إذا كانت فاتورة جملة استخدم سعر الجملة إن وجد وإلا السعر الافتراضي
      if (product.wholesalePrice !== undefined && product.wholesalePrice !== null) {
        return product.wholesalePrice;
      }
      if (product.posPrice !== undefined && product.posPrice !== null) {
        return product.posPrice;
      }
      return product.sellingPrice;
    } else {
      // إذا كانت فاتورة عادية استخدم سعر نقطة البيع إن وجد وإلا السعر الافتراضي
      if (product.posPrice !== undefined && product.posPrice !== null) {
        return product.posPrice;
      }
      return product.sellingPrice;
    }
  };

  // دالة لتحديد نوع السعر المستخدم
  const getPriceType = (product: InventoryItem): string => {
    if (invoiceType === 'wholesale') {
      if (product.wholesalePrice !== undefined && product.wholesalePrice !== null) {
        return 'سعر الجملة';
      }
      if (product.posPrice !== undefined && product.posPrice !== null) {
        return 'سعر نقطة البيع';
      }
      return 'السعر الافتراضي';
    } else {
      if (product.posPrice !== undefined && product.posPrice !== null) {
        return 'سعر نقطة البيع';
      }
      return 'السعر الافتراضي';
    }
  };

const addToCart = (product: InventoryItem) => {
    const existingItemIndex = cartItems.findIndex(item => item.product.id === product.id);
    const price = getProductPrice(product); // استخدام الدالة الجديدة
    const priceType = getPriceType(product);
    console.log(`إضافة منتج: ${product.name}, السعر: ${price}, نوع السعر: ${priceType}, نوع الفاتورة: ${invoiceType}`);

    if (existingItemIndex !== -1) {
      // زيادة كمية المنتج الموجود في السلة
      const updatedCartItems = [...cartItems];
      const currentQuantity = updatedCartItems[existingItemIndex].quantity;

      // التحقق من عدم تجاوز الكمية المتاحة
      if (currentQuantity >= product.quantity) {
        setSnackbar({
          open: true,
          message: 'عذراً، لا يمكن إضافة المزيد من هذا المنتج (الكمية المتاحة غير كافية)',
          severity: 'warning'
        });
        return;
      }

      updatedCartItems[existingItemIndex] = {
        ...updatedCartItems[existingItemIndex],
        quantity: currentQuantity + 1,
        total: (currentQuantity + 1) * price // استخدام السعر المحسوب
      };
      setCartItems(updatedCartItems);
    } else {
      // إضافة منتج جديد إلى السلة
      setCartItems([
        ...cartItems,
        {
          product,
          quantity: 1,
          total: price // استخدام السعر المحسوب
        }
      ]);
    }
  };

  // تقليل كمية منتج في السلة
  const decreaseQuantity = (productId: string) => {
    const existingItemIndex = cartItems.findIndex(item => item.product.id === productId);

    if (existingItemIndex !== -1) {
      const updatedCartItems = [...cartItems];
      const currentQuantity = updatedCartItems[existingItemIndex].quantity;

      const price = getProductPrice(updatedCartItems[existingItemIndex].product); // استخدام الدالة الجديدة

      if (currentQuantity === 1) {
        // إزالة المنتج من السلة إذا كانت الكمية ستصبح صفراً
        updatedCartItems.splice(existingItemIndex, 1);
      } else {
        // تقليل الكمية
        updatedCartItems[existingItemIndex] = {
          ...updatedCartItems[existingItemIndex],
          quantity: currentQuantity - 1,
          total: (currentQuantity - 1) * price // استخدام السعر المحسوب
        };
      }

      setCartItems(updatedCartItems);
    }
  };

  // إزالة منتج من السلة
  const removeFromCart = (productId: string) => {
    setCartItems(cartItems.filter(item => item.product.id !== productId));
  };

  // حساب إجمالي الفاتورة
  const calculateTotal = () => {
    const subtotal = cartItems.reduce((sum, item) => sum + item.total, 0);
    const finalTotal = subtotal - discount;
    const remaining = finalTotal - amountPaid;
    return {
      subtotal,
      discount,
      total: finalTotal > 0 ? finalTotal : 0,
      remaining: isPartialPayment ? (remaining > 0 ? remaining : 0) : 0
    };
  };

  // معالجة اختيار العميل
  const handleCustomerSelect = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    if (customer) {
      setCustomerName(customer.name);
    } else {
      setCustomerName('');
    }
  };

  // فتح نافذة إضافة عميل جديد
  const handleOpenAddCustomerDialog = () => {
    setNewCustomerData({
      name: customerName || '',
      phone: '',
      email: '',
      address: ''
    });
    setOpenAddCustomerDialog(true);
  };

  // إغلاق نافذة إضافة عميل جديد
  const handleCloseAddCustomerDialog = () => {
    setOpenAddCustomerDialog(false);
  };

  // إضافة عميل جديد
  const handleAddCustomer = async () => {
    try {
      if (!newCustomerData.name || !newCustomerData.phone) {
        setSnackbar({
          open: true,
          message: 'يرجى إدخال اسم العميل ورقم الهاتف',
          severity: 'error'
        });
        return;
      }

      const newCustomer = await addCustomer(
        newCustomerData.name,
        newCustomerData.phone,
        newCustomerData.email,
        newCustomerData.address,
        selectedBranch?.id || 'default',
        ''
      );

      // تحديث قائمة العملاء
      await loadCustomers();

      // اختيار العميل الجديد
      if (newCustomer) {
        setSelectedCustomer(newCustomer);
        setCustomerName(newCustomer.name);
      }

      setSnackbar({
        open: true,
        message: 'تمت إضافة العميل بنجاح',
        severity: 'success'
      });

      handleCloseAddCustomerDialog();
    } catch (error) {
      console.error('Error adding customer:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء إضافة العميل',
        severity: 'error'
      });
    }
  };

  // فتح نافذة الدفع
  const handleOpenPaymentDialog = () => {
    if (cartItems.length === 0) {
      setSnackbar({
        open: true,
        message: 'لا يمكن إتمام الطلب. السلة فارغة',
        severity: 'error'
      });
      return;
    }

    const { total } = calculateTotal();
    setAmountPaid(total);
    setIsPartialPayment(false);

    // تعيين تاريخ الاستحقاق الافتراضي إلى شهر من اليوم
    const defaultDueDate = new Date();
    defaultDueDate.setMonth(defaultDueDate.getMonth() + 1);
    setDueDate(defaultDueDate.toISOString().split('T')[0]);

    setOpenPaymentDialog(true);
  };

  // إغلاق نافذة الدفع
  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
  };

  // معالجة وإتمام الطلب
  const handleCheckout = async () => {
    try {
      // التحقق من رقم المحفظة للمحافظ الإلكترونية
      if (electronicWalletMethods.includes(paymentMethod) && !walletNumber.trim()) {
        setSnackbar({
          open: true,
          message: 'يرجى إدخال رقم المحفظة/الحساب المحول عليه',
          severity: 'error'
        });
        return;
      }

      setProcessingOrder(true);
      handleClosePaymentDialog();

      // التحقق من وجود مخزن محدد
      if (!selectedWarehouse) {
        setSnackbar({
          open: true,
          message: 'يجب اختيار مخزن لإتمام عملية البيع',
          severity: 'error'
        });
        setProcessingOrder(false);
        return;
      }

      // التحقق من صلاحية الوصول للمخزن
      console.log('🔐 Checking warehouse access for checkout...');
      console.log('👤 Current user:', currentUser);
      console.log('🏪 Selected warehouse:', selectedWarehouse);
      console.log('🔑 User allowedWarehouses:', currentUser?.allowedWarehouses);
      console.log('🔑 User role:', currentUser?.role);
      console.log('🔑 User branchId:', currentUser?.branchId);
      
      const hasWarehouseAccess = canAccessWarehouse(currentUser, selectedWarehouse.id);
      console.log('✅ Has warehouse access:', hasWarehouseAccess);
      
      // إصلاح مؤقت: السماح بالوصول إذا كان المستخدم مدير أو إذا لم تكن هناك مخازن مسموح بها
      const shouldAllowAccess = currentUser?.role === 'ADMIN' || 
                               !currentUser?.allowedWarehouses || 
                               currentUser.allowedWarehouses.length === 0 ||
                               hasWarehouseAccess;
      
      if (!shouldAllowAccess) {
        console.error('❌ Warehouse access denied');
        setSnackbar({
          open: true,
          message: 'ليس لديك صلاحية للوصول إلى هذا المخزن',
          severity: 'error'
        });
        setProcessingOrder(false);
        return;
      }
      
      console.log('✅ Warehouse access granted');

      // التحقق من وجود عميل محدد في حالة الدفع الجزئي
      if (isPartialPayment && !selectedCustomer) {
        setSnackbar({
          open: true,
          message: 'يجب اختيار عميل لتسجيل الدين',
          severity: 'error'
        });
        setProcessingOrder(false);
        return;
      }

      const { subtotal, total, remaining } = calculateTotal();

      // تحويل عناصر السلة إلى تنسيق SaleItem
      const saleItems: SaleItem[] = cartItems.map(item => ({
        itemId: item.product.id,
        name: item.product.name,
        quantity: item.quantity,
        price: item.product.sellingPrice,
        total: item.total,
        warehouseId: item.product.warehouseId || selectedWarehouse?.id || 'default'
      }));

      // تحديد حالة الدفع
      const paymentStatus: PaymentStatus = isPartialPayment ? 'partiallyPaid' : 'paid';

      // إنشاء الطلب
      const newSale = await createSale(
        saleItems,
        subtotal,
        discount,
        0, // ضريبة
        paymentMethod as PaymentMethod,
        paymentStatus,
        currentUser?.id || 'unknown',
        currentUser?.name || 'مستخدم',
        selectedBranch?.id || 'default',
        selectedWarehouse?.id || 'default',
        selectedCustomer?.id || '', // معرف العميل
        selectedCustomer?.name || customerName || 'عميل عام',
        '', // ملاحظات
        isPartialPayment ? amountPaid : total, // المبلغ المدفوع
        isPartialPayment ? remaining : 0, // المبلغ المتبقي
        isPartialPayment, // هل هذه عملية بيع بالدين
        dueDate ? new Date(dueDate) : undefined // تاريخ استحقاق الدفع
      );

      // ملاحظة: لا نحتاج لإنشاء دين منفصل هنا لأن دالة createSale تقوم بذلك تلقائياً عندما يكون isDebt=true

      // إعادة تعيين حالة السلة والفاتورة
      setCartItems([]);
      setSelectedCustomer(null);
      setCustomerName('');
      setDiscount(0);
      setSearchTerm('');
      setAmountPaid(0);
      setIsPartialPayment(false);
      setDueDate('');

      // إعادة تحميل المنتجات لتحديث الكميات المتاحة
      loadProducts();

      // عرض الفاتورة الجديدة
      if (newSale) {
        setSelectedSale(newSale);
        setInvoiceDialogOpen(true);
      }

      // عرض رسالة نجاح مناسبة
      setSnackbar({
        open: true,
        message: isPartialPayment && remaining > 0 ?
          `تم إتمام عملية البيع بنجاح وتسجيل دين بقيمة ${formatCurrency(remaining)} على العميل` :
          'تم إتمام عملية البيع بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error processing sale:', error);
      setSnackbar({
        open: true,
        message: `حدث خطأ أثناء معالجة عملية البيع: ${error instanceof Error ? error.message : 'خطأ غير معروف'}`,
        severity: 'error'
      });
    } finally {
      setProcessingOrder(false);
    }
  };

  // إغلاق رسالة التنبيه
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // فتح فاتورة البيع
  const handleShowInvoice = (sale: any) => {
    setSelectedSale(sale);
    setInvoiceDialogOpen(true);
  };

  // فتح نافذة المرتجعات
  const handleShowReturn = (saleId: string) => {
    // البحث عن البيع في المبيعات الحديثة أو تحميله من قاعدة البيانات
    const sale = recentSales.find(s => s.id === saleId);
    if (sale) {
      setSelectedSale(sale);
      setReturnDialogOpen(true);
    }
  };

  // معالجة إرجاع المنتجات (النظام القديم)
  const handleReturn = async (returnData: any) => {
    try {
      await createReturn(returnData);

      // إعادة تحميل المنتجات لتحديث المخزون
      loadProducts();

      setSnackbar({
        open: true,
        message: 'تم إرجاع المنتجات بنجاح وتحديث المخزون',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error processing return:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء معالجة الإرجاع',
        severity: 'error'
      });
    }
  };

  // معالجة المرتجع الكامل (النظام الجديد)
  const handleFullReturn = async (saleId: string, reason: string, refundMethod: RefundMethod) => {
    try {
      if (!currentUser) throw new Error('المستخدم غير مسجل الدخول');

      await createFullReturn(
        saleId,
        reason,
        refundMethod,
        currentUser.id,
        currentUser.name || 'موظف',
        `ملاحظات: ${reason}`
      );

      // إعادة تحميل البيانات
      loadProducts();
      loadSales();

      setSnackbar({
        open: true,
        message: `تم إنشاء المرتجع الكامل بنجاح - ${refundMethod === RefundMethod.CASH ? 'استرداد نقدي' : 'رصيد للعميل'}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating full return:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء إنشاء المرتجع الكامل',
        severity: 'error'
      });
      throw error;
    }
  };

  // معالجة المرتجع الجزئي (النظام الجديد)
  const handlePartialReturn = async (saleId: string, items: any[], reason: string, refundMethod: RefundMethod) => {
    try {
      if (!currentUser) throw new Error('المستخدم غير مسجل الدخول');

      // تحويل العناصر إلى التنسيق المطلوب
      const returnItems = items.map(item => ({
        itemId: item.itemId,
        name: item.name,
        sku: item.sku || '',
        quantity: item.returnQuantity,
        price: item.price,
        total: item.price * item.returnQuantity,
        warehouseId: item.warehouseId,
        returnReason: item.returnReason
      }));

      await createPartialReturn(
        saleId,
        returnItems,
        reason,
        refundMethod,
        currentUser.id,
        currentUser.name || 'موظف',
        `ملاحظات: ${reason}`
      );

      // إعادة تحميل البيانات
      loadProducts();
      loadSales();

      setSnackbar({
        open: true,
        message: `تم إنشاء المرتجع الجزئي بنجاح - ${refundMethod === RefundMethod.CASH ? 'استرداد نقدي' : 'رصيد للعميل'}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating partial return:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء إنشاء المرتجع الجزئي',
        severity: 'error'
      });
      throw error;
    }
  };

  // دالة تشخيص المرتجعات
  const handleDebugReturns = async () => {
    try {
      await debugReturns();
      setSnackbar({
        open: true,
        message: 'تم تشغيل تشخيص المرتجعات - تحقق من الكونسول',
        severity: 'info'
      });
    } catch (error) {
      console.error('Error debugging returns:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تشخيص المرتجعات',
        severity: 'error'
      });
    }
  };

  const { subtotal, total } = calculateTotal();

  return (
    <Box sx={{
      p: { xs: 1, md: 3 },
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh'
    }}>
      {/* شريط الأدوات العلوي */}
      <Paper
        elevation={4}
        sx={{
          p: 2,
          mb: 2,
          background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
          borderRadius: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography variant="h5" fontWeight="bold" color="primary">
          🛍️ نقطة البيع
        </Typography>

        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ReceiptIcon />}
            onClick={() => {
              window.open('/admin/invoices', '_blank');
            }}
            sx={{ borderRadius: 2 }}
          >
            الفواتير
          </Button>

          <Button
            variant="outlined"
            color="warning"
            startIcon={<ReturnIcon />}
            onClick={() => {
              window.open('/admin/invoices', '_blank');
            }}
            sx={{ borderRadius: 2 }}
          >
            المرتجعات
          </Button>

          <Button
            variant="outlined"
            color="info"
            startIcon={<HistoryIcon />}
            onClick={() => {
              // يمكن إضافة منطق لعرض تاريخ المبيعات
              setSnackbar({
                open: true,
                message: 'سيتم إضافة تاريخ المبيعات قريباً',
                severity: 'info'
              });
            }}
            sx={{ borderRadius: 2 }}
          >
            تاريخ المبيعات
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={2}>

        {/* القسم الأيسر - قائمة المنتجات */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={8}
            sx={{
              p: 3,
              height: '100%',
              minHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
              borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}>

            {/* البحث وشريط التصفية */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h5"
                sx={{
                  mb: 3,
                  background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}
              >
                🛍️ المنتجات المتاحة
              </Typography>

              <TextField
                fullWidth
                placeholder="🔍 البحث عن منتج..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                    borderRadius: 3,
                    '&:hover': {
                      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.15)'
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)'
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#667eea' }} />
                    </InputAdornment>
                  )
                }}
              />


              {/* اختيار نوع الفاتورة */}
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>نوع الفاتورة</InputLabel>
                  <Select
                    value={invoiceType}
                    label="نوع الفاتورة"
                    onChange={(e) => {
                      const newType = e.target.value as 'retail' | 'wholesale';
                      setInvoiceType(newType);
                      
                      // إعادة حساب أسعار السلة عند تغيير نوع الفاتورة
                      const updatedCart = cartItems.map(item => ({
                        ...item,
                        total: item.quantity * getProductPrice(item.product)
                      }));
                      setCartItems(updatedCart);
                    }}
                  >
                    <MenuItem value="retail">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptIcon />
                        <Typography>فاتورة عادية (تجزئة)</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="wholesale">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MoneyIcon />
                        <Typography>فاتورة جملة</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* شريط الفئات */}
              <Box sx={{ mb: 2, mt: 2, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" sx={{ mr: 2 }}>
                    الفئات:
                  </Typography>

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => loadCategories()}
                    sx={{
                      mr: 1,
                      mb: 1,
                      background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                      color: 'white',
                      border: 'none',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #764ba2 30%, #667eea 90%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                      }
                    }}
                  >
                    🔄 إعادة تحميل الفئات
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => loadProducts()}
                    sx={{
                      mr: 1,
                      mb: 1,
                      background: 'linear-gradient(45deg, #f093fb 0%, #f5576c 100%)',
                      color: 'white',
                      border: 'none',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #f5576c 0%, #f093fb 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 15px rgba(245, 87, 108, 0.3)'
                      }
                    }}
                  >
                    📦 إعادة تحميل المنتجات
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleDebugReturns}
                    sx={{
                      mr: 1,
                      mb: 1,
                      background: 'linear-gradient(45deg, #ff9a9e 0%, #fecfef 100%)',
                      color: 'white',
                      border: 'none',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #fecfef 0%, #ff9a9e 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 15px rgba(255, 154, 158, 0.3)'
                      }
                    }}
                  >
                    🔧 تشخيص المرتجعات
                  </Button>

                  {categories.length === 0 && (
                    <Typography variant="caption" color="error" sx={{ ml: 2, alignSelf: 'center', mb: 1 }}>
                      لم يتم العثور على فئات. يرجى إضافة فئات أو إعادة التحميل.
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    icon={<CategoryIcon />}
                    label={`🏪 جميع الفئات (${products.length})`}
                    onClick={() => setSelectedCategoryId('')}
                    sx={{
                      mb: 1,
                      background: selectedCategoryId === ''
                        ? 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)'
                        : 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                      color: selectedCategoryId === '' ? 'white' : '#667eea',
                      border: selectedCategoryId === '' ? 'none' : '2px solid #667eea',
                      fontWeight: 'bold',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                        background: selectedCategoryId === ''
                          ? 'linear-gradient(45deg, #764ba2 30%, #667eea 90%)'
                          : 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                        color: 'white'
                      }
                    }}
                  />

                  {/* عرض الفئات المتاحة */}
                  {categories.map((category) => {
                    // حساب عدد المنتجات في هذه الفئة
                    const productCount = products.filter(product =>
                      product.categoryId === category.id ||
                      (product.category && product.category === category.name)
                    ).length;

                    // عرض جميع الفئات حتى لو لم يكن لها منتجات
                    const categoryColors = [
                      'linear-gradient(45deg, #f093fb 0%, #f5576c 100%)',
                      'linear-gradient(45deg, #4facfe 0%, #00f2fe 100%)',
                      'linear-gradient(45deg, #43e97b 0%, #38f9d7 100%)',
                      'linear-gradient(45deg, #fa709a 0%, #fee140 100%)',
                      'linear-gradient(45deg, #a8edea 0%, #fed6e3 100%)',
                      'linear-gradient(45deg, #ff9a9e 0%, #fecfef 100%)'
                    ];
                    const colorIndex = categories.indexOf(category) % categoryColors.length;

                    return (
                      <Chip
                        key={category.id}
                        label={`${category.name} (${productCount})`}
                        onClick={() => handleCategoryChange(category.id)}
                        sx={{
                          mb: 1,
                          background: selectedCategoryId === category.id
                            ? categoryColors[colorIndex]
                            : 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                          color: selectedCategoryId === category.id ? 'white' : '#667eea',
                          border: selectedCategoryId === category.id ? 'none' : '2px solid #e0e0e0',
                          fontWeight: 'bold',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                            background: categoryColors[colorIndex],
                            color: 'white'
                          }
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* عرض المنتجات */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                <CircularProgress />
              </Box>
            ) : filteredProducts.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                <Typography variant="h6" color="textSecondary">
                  لا توجد منتجات متطابقة مع معايير البحث
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                <Grid container spacing={2}>
                  {filteredProducts.map((product) => (
                    <Grid item xs={6} sm={4} md={3} lg={2} xl={2} key={product.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.3s ease-in-out',
                          background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                          borderRadius: 3,
                          border: '1px solid rgba(102, 126, 234, 0.1)',
                          '&:hover': {
                            transform: 'translateY(-8px) scale(1.02)',
                            boxShadow: '0 12px 40px rgba(102, 126, 234, 0.2)',
                            background: 'linear-gradient(145deg, #f8f9fa 0%, #ffffff 100%)',
                            border: '1px solid rgba(102, 126, 234, 0.3)'
                          }
                        }}
                        onClick={() => addToCart(product)}
                      >
                        <CardMedia
                          component="img"
                          height="80"
                          image={product.image || 'https://via.placeholder.com/150'}
                          alt={product.name}
                          sx={{ objectFit: 'contain', p: 0.5 }}
                        />
                        <CardContent sx={{ p: 1.5, pb: '12px !important' }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 'bold',
                              color: '#2c3e50',
                              mb: 0.5,
                              fontSize: '0.85rem',
                              lineHeight: 1.2,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              minHeight: '2.4em'
                            }}
                          >
                            {product.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              mb: 0.5,
                              fontSize: '0.7rem',
                              display: 'block'
                            }}
                          >
                            📂 {product.category || 'بدون فئة'}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 'bold',
                                  background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                                  backgroundClip: 'text',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                  fontSize: '0.8rem'
                                }}
                              >
                                {formatCurrency(getProductPrice(product))}
                              </Typography>
                              <Chip
                                size="small"
                                label={`${product.quantity}`}
                              sx={{
                                height: '20px',
                                minWidth: '35px',
                                background: product.quantity > 10
                                  ? 'linear-gradient(45deg, #43e97b 0%, #38f9d7 100%)'
                                  : product.quantity > 3
                                  ? 'linear-gradient(45deg, #fa709a 0%, #fee140 100%)'
                                  : 'linear-gradient(45deg, #f093fb 0%, #f5576c 100%)',
                                color: 'white',
                                fontWeight: 'bold',
                                '& .MuiChip-label': {
                                  px: 0.5,
                                  fontSize: '0.65rem'
                                }
                              }}
                            />
                            </Box>
                            {/* عرض نوع السعر المستخدم */}
                            <Chip
                              size="small"
                              label={getPriceType(product)}
                              sx={{
                                height: '18px',
                                fontSize: '0.6rem',
                                background: invoiceType === 'wholesale'
                                  ? 'linear-gradient(45deg, #FFA726 30%, #FB8C00 90%)'
                                  : 'linear-gradient(45deg, #42A5F5 30%, #1E88E5 90%)',
                                color: 'white',
                                fontWeight: 'bold',
                                '& .MuiChip-label': {
                                  px: 0.5
                                }
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* قسم السلة والدفع */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={8}
            sx={{
              p: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
              borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}
          >
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                background: 'linear-gradient(45deg, #f093fb 0%, #f5576c 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 'bold',
                textAlign: 'center',
                mb: 3
              }}
            >
              🛒 سلة المشتريات
            </Typography>

            <Box sx={{ mb: 2 }}>
              {/* اختيار المخزن */}
              <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                <InputLabel>المخزن</InputLabel>
                <Select
                  value={selectedWarehouse?.id || ''}
                  onChange={(e) => {
                    const warehouse = warehouses.find(w => w.id === e.target.value);
                    setSelectedWarehouse(warehouse || null);
                  }}
                  label="المخزن"
                  startAdornment={
                    <InputAdornment position="start">
                      🏪
                    </InputAdornment>
                  }
                >
                  {warehouses.map((warehouse) => (
                    <MenuItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.name} - {warehouse.branchName || 'فرع غير محدد'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* اختيار العميل */}
              <Autocomplete
                options={customers}
                getOptionLabel={(option) => `${option.name} - ${option.phone}`}
                value={selectedCustomer}
                onChange={(_, newValue) => handleCustomerSelect(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="العميل"
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <PersonIcon />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  size="small"
                  startIcon={<PersonAddIcon />}
                  onClick={handleOpenAddCustomerDialog}
                >
                  إضافة عميل جديد
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {cartItems.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  السلة فارغة
                </Typography>
              </Box>
            ) : (
              <List sx={{ flexGrow: 1, maxHeight: 300, overflow: 'auto' }}>
                {cartItems.map((item) => (
                  <ListItem
                    key={item.product.id}
                    secondaryAction={
                      <IconButton edge="end" onClick={() => removeFromCart(item.product.id)}>
                        <DeleteIcon />
                      </IconButton>
                    }
                    sx={{ py: 1 }}
                  >
                    <ListItemText
                      primary={item.product.name}
                      secondary={`السعر: ${formatCurrency(item.product.sellingPrice)}`}
                    />
                    <Box display="flex" alignItems="center" sx={{ minWidth: 100 }}>
                      <IconButton size="small" onClick={() => decreaseQuantity(item.product.id)}>
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="body2" sx={{ mx: 1 }}>
                        {item.quantity}
                      </Typography>
                      <IconButton size="small" onClick={() => addToCart(item.product)}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}

            <Divider sx={{ my: 2 }} />

            <Box mb={2}>
              <TextField
                label="خصم"
                type="number"
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">{DEFAULT_CURRENCY_SYMBOL}</InputAdornment>
                }}
                value={discount}
                onChange={(e) => setDiscount(Math.max(0, Number(e.target.value)))}
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                <InputLabel>طريقة الدفع</InputLabel>
                <Select
                  value={paymentMethod}
                  onChange={(e) => {
                    setPaymentMethod(e.target.value);
                    if (!electronicWalletMethods.includes(e.target.value)) {
                      setWalletNumber('');
                    }
                  }}
                  label="طريقة الدفع"
                >
                  <MenuItem value="cash">
                    <Box display="flex" alignItems="center" gap={1}>
                      <MoneyIcon fontSize="small" />
                      <span>نقداً</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="card">
                    <Box display="flex" alignItems="center" gap={1}>
                      <CreditCardIcon fontSize="small" />
                      <span>بطاقة ائتمان</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="vodafone_wallet">
                    <Box display="flex" alignItems="center" gap={1}>
                      <PhoneIphoneIcon fontSize="small" />
                      <span>محفظة فودافون</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="instapay">
                    <Box display="flex" alignItems="center" gap={1}>
                      <PhoneIphoneIcon fontSize="small" />
                      <span>انستاباي</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="etisalat_wallet">
                    <Box display="flex" alignItems="center" gap={1}>
                      <PhoneIphoneIcon fontSize="small" />
                      <span>اتصالات كاش</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="bankTransfer">
                    <Box display="flex" alignItems="center" gap={1}>
                      <AccountBalanceIcon fontSize="small" />
                      <span>تحويل بنكي</span>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {/* حقل رقم المحفظة - يظهر فقط للمحافظ الإلكترونية */}
              {electronicWalletMethods.includes(paymentMethod) && (
                <TextField
                  fullWidth
                  size="small"
                  label="رقم المحفظة/الحساب"
                  value={walletNumber}
                  onChange={(e) => setWalletNumber(e.target.value)}
                  placeholder={
                    paymentMethod === 'vodafone_wallet' ? '01xxxxxxxxx' :
                    paymentMethod === 'instapay' ? '1234567890123456' :
                    '01xxxxxxxxx'
                  }
                  sx={{ mb: 2 }}
                  helperText={`مطلوب لتأكيد ${
                    paymentMethod === 'vodafone_wallet' ? 'تحويل فودافون كاش' :
                    paymentMethod === 'instapay' ? 'التحويل البنكي' :
                    'تحويل اتصالات كاش'
                  }`}
                />
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1">المجموع الفرعي:</Typography>
                <Typography variant="body1">{formatCurrency(subtotal)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1">الخصم:</Typography>
                <Typography variant="body1">{formatCurrency(discount)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6">الإجمالي:</Typography>
                <Typography variant="h6" color="primary.main">{formatCurrency(total)}</Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              fullWidth
              size="large"
              disabled={cartItems.length === 0 || processingOrder}
              onClick={handleOpenPaymentDialog}
              sx={{
                mt: 'auto',
                py: 2,
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                borderRadius: 3,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #764ba2 30%, #667eea 90%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4)'
                },
                '&:disabled': {
                  background: 'linear-gradient(45deg, #e0e0e0 30%, #bdbdbd 90%)',
                  color: '#9e9e9e'
                }
              }}
              startIcon={<PaymentIcon />}
            >
              {processingOrder ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                '💳 إتمام عملية البيع'
              )}
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* نافذة إضافة عميل جديد */}
      <Dialog
        open={openAddCustomerDialog}
        onClose={handleCloseAddCustomerDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold',
            textAlign: 'center',
            fontSize: '1.5rem'
          }}
        >
          👤 إضافة عميل جديد
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="الاسم"
                fullWidth
                required
                value={newCustomerData.name}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="رقم الهاتف"
                fullWidth
                required
                value={newCustomerData.phone}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="البريد الإلكتروني"
                fullWidth
                value={newCustomerData.email}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="العنوان"
                fullWidth
                value={newCustomerData.address}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, address: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseAddCustomerDialog}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              background: 'linear-gradient(45deg, #e0e0e0 30%, #bdbdbd 90%)',
              color: '#424242',
              '&:hover': {
                background: 'linear-gradient(45deg, #bdbdbd 30%, #e0e0e0 90%)'
              }
            }}
          >
            ❌ إلغاء
          </Button>
          <Button
            onClick={handleAddCustomer}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              background: 'linear-gradient(45deg, #43e97b 0%, #38f9d7 100%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #38f9d7 0%, #43e97b 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(67, 233, 123, 0.3)'
              }
            }}
          >
            ✅ إضافة
          </Button>
        </DialogActions>
      </Dialog>

      {/* نافذة الدفع */}
      <Dialog
        open={openPaymentDialog}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(45deg, #f093fb 0%, #f5576c 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold',
            textAlign: 'center',
            fontSize: '1.5rem'
          }}
        >
          💳 إتمام عملية البيع
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                المبلغ الإجمالي: {formatCurrency(calculateTotal().total)}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPartialPayment}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsPartialPayment(e.target.checked)}
                  />
                }
                label="دفع جزئي (تسجيل دين على العميل)"
              />
            </Grid>

            {isPartialPayment && (
              <>
                <Grid item xs={12}>
                  <TextField
                    label="المبلغ المدفوع"
                    type="number"
                    fullWidth
                    required
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(Math.max(0, Math.min(calculateTotal().total, Number(e.target.value))))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{DEFAULT_CURRENCY_SYMBOL}</InputAdornment>
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color={selectedCustomer ? 'textPrimary' : 'error'}>
                    المبلغ المتبقي: {formatCurrency(calculateTotal().remaining)}
                  </Typography>
                  {!selectedCustomer && (
                    <Typography variant="caption" color="error">
                      يجب اختيار عميل لتسجيل الدين
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="تاريخ الاستحقاق"
                    type="date"
                    fullWidth
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    InputProps={{
                      inputProps: { min: new Date().toISOString().split('T')[0] }
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>طريقة الدفع</InputLabel>
                <Select
                  value={paymentMethod}
                  onChange={(e) => {
                    setPaymentMethod(e.target.value);
                    if (!electronicWalletMethods.includes(e.target.value)) {
                      setWalletNumber('');
                    }
                  }}
                  label="طريقة الدفع"
                >
                  <MenuItem value="cash">
                    <Box display="flex" alignItems="center" gap={1}>
                      <MoneyIcon fontSize="small" />
                      <Box>
                        <Typography variant="body2">نقداً</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Cash
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                  <MenuItem value="card">
                    <Box display="flex" alignItems="center" gap={1}>
                      <CreditCardIcon fontSize="small" />
                      <Box>
                        <Typography variant="body2">بطاقة ائتمان</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Credit Card
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                  <MenuItem value="vodafone_wallet">
                    <Box display="flex" alignItems="center" gap={1}>
                      <PhoneIphoneIcon fontSize="small" />
                      <Box>
                        <Typography variant="body2">محفظة فودافون</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Vodafone Cash
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                  <MenuItem value="instapay">
                    <Box display="flex" alignItems="center" gap={1}>
                      <PhoneIphoneIcon fontSize="small" />
                      <Box>
                        <Typography variant="body2">انستاباي</Typography>
                        <Typography variant="caption" color="text.secondary">
                          InstaPay
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                  <MenuItem value="etisalat_wallet">
                    <Box display="flex" alignItems="center" gap={1}>
                      <PhoneIphoneIcon fontSize="small" />
                      <Box>
                        <Typography variant="body2">اتصالات كاش</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Etisalat Cash
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                  <MenuItem value="bankTransfer">
                    <Box display="flex" alignItems="center" gap={1}>
                      <AccountBalanceIcon fontSize="small" />
                      <Box>
                        <Typography variant="body2">تحويل بنكي</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Bank Transfer
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* حقل رقم المحفظة - يظهر فقط للمحافظ الإلكترونية */}
            {electronicWalletMethods.includes(paymentMethod) && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="رقم المحفظة/الحساب المحول عليه"
                  value={walletNumber}
                  onChange={(e) => setWalletNumber(e.target.value)}
                  placeholder={
                    paymentMethod === 'vodafone_wallet' ? '01xxxxxxxxx' :
                    paymentMethod === 'instapay' ? '1234567890123456' :
                    '01xxxxxxxxx'
                  }
                  required
                  helperText={`مطلوب لتأكيد ${
                    paymentMethod === 'vodafone_wallet' ? 'تحويل فودافون كاش' :
                    paymentMethod === 'instapay' ? 'التحويل البنكي' :
                    'تحويل اتصالات كاش'
                  }`}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleClosePaymentDialog}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              background: 'linear-gradient(45deg, #e0e0e0 30%, #bdbdbd 90%)',
              color: '#424242',
              '&:hover': {
                background: 'linear-gradient(45deg, #bdbdbd 30%, #e0e0e0 90%)'
              }
            }}
          >
            ❌ إلغاء
          </Button>
          <Button
            onClick={handleCheckout}
            variant="contained"
            disabled={isPartialPayment && (!selectedCustomer || calculateTotal().remaining <= 0 || amountPaid <= 0)}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #764ba2 30%, #667eea 90%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
              },
              '&:disabled': {
                background: 'linear-gradient(45deg, #e0e0e0 30%, #bdbdbd 90%)',
                color: '#9e9e9e'
              }
            }}
          >
            {isPartialPayment ? '💰 إتمام البيع وتسجيل الدين' : '✅ إتمام البيع'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار الفاتورة */}
      <InvoiceDialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
        sale={selectedSale}
        onReturn={handleShowReturn}
        showReturnButton={true}
      />

      {/* حوار المرتجعات المحسن */}
      <EnhancedReturnDialog
        open={returnDialogOpen}
        onClose={() => setReturnDialogOpen(false)}
        sale={selectedSale}
        onFullReturn={handleFullReturn}
        onPartialReturn={handlePartialReturn}
      />

      {/* حوار المرتجعات القديم (احتياطي) */}
      {/* <ReturnDialog
        open={false}
        onClose={() => setReturnDialogOpen(false)}
        sale={selectedSale}
        onReturn={handleReturn}
      /> */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default POSPage;
